import { useState, useMemo } from "react";
import { Link2, RefreshCw, Trash2, Plus, CheckCircle2, Clock, Copy, ChevronDown, Building2 } from "lucide-react";
import { useGetICalFeeds, useCreateICalFeed, useDeleteICalFeed, useGetStrutture, useGetCamere } from "@/lib/supabaseQueries";

type Piattaforma = "Airbnb" | "Booking.com" | "Vrbo" | "TripAdvisor" | "Expedia" | "Altro";

const PIATTAFORME: { id: Piattaforma; label: string; logo: React.ReactNode }[] = [
  {
    id: "Airbnb",
    label: "Airbnb",
    logo: (
      <svg viewBox="0 0 32 32" className="h-5 w-5" fill="none">
        <path d="M16 3C9.925 3 5 7.925 5 14c0 4.556 4.09 9.637 7.41 13.02.97.987 1.793 1.77 2.29 2.245.17.16.395.247.627.247s.458-.087.627-.247c.497-.474 1.32-1.258 2.29-2.245C21.91 23.637 27 18.556 27 14c0-6.075-4.925-11-11-11zm0 15.5c-2.485 0-4.5-2.015-4.5-4.5S13.515 9.5 16 9.5s4.5 2.015 4.5 4.5-2.015 4.5-4.5 4.5z" fill="#FF5A5F"/>
      </svg>
    ),
  },
  {
    id: "Booking.com",
    label: "Booking.com",
    logo: (
      <svg viewBox="0 0 32 32" className="h-5 w-5" fill="none">
        <rect width="32" height="32" rx="6" fill="#003580"/>
        <text x="5" y="22" fontSize="11" fontWeight="bold" fill="white" fontFamily="sans-serif">BK</text>
      </svg>
    ),
  },
  {
    id: "Vrbo",
    label: "Vrbo",
    logo: (
      <svg viewBox="0 0 32 32" className="h-5 w-5" fill="none">
        <rect width="32" height="32" rx="6" fill="#1B5EF7"/>
        <text x="4" y="22" fontSize="11" fontWeight="bold" fill="white" fontFamily="sans-serif">VRBO</text>
      </svg>
    ),
  },
  {
    id: "TripAdvisor",
    label: "TripAdvisor",
    logo: (
      <svg viewBox="0 0 32 32" className="h-5 w-5" fill="none">
        <circle cx="16" cy="16" r="14" fill="#34E0A1"/>
        <circle cx="10" cy="16" r="4" fill="white"/>
        <circle cx="22" cy="16" r="4" fill="white"/>
        <circle cx="10" cy="16" r="2" fill="black"/>
        <circle cx="22" cy="16" r="2" fill="black"/>
      </svg>
    ),
  },
  {
    id: "Expedia",
    label: "Expedia",
    logo: (
      <svg viewBox="0 0 32 32" className="h-5 w-5" fill="none">
        <rect width="32" height="32" rx="6" fill="#FFC72C"/>
        <text x="4" y="22" fontSize="10" fontWeight="bold" fill="#1C2B4B" fontFamily="sans-serif">EXP</text>
      </svg>
    ),
  },
  {
    id: "Altro",
    label: "Altro",
    logo: (
      <div className="h-5 w-5 rounded bg-muted flex items-center justify-center">
        <Link2 className="h-3 w-3 text-muted-foreground" />
      </div>
    ),
  },
];

const piattaformaLogo = (p: string) => PIATTAFORME.find(x => x.id === p)?.logo ?? PIATTAFORME[5].logo;

export default function ICalSync() {
  const { data: feeds = [] } = useGetICalFeeds();
  const { data: strutture = [] } = useGetStrutture();
  const { data: tutteLeCamere = [] } = useGetCamere();
  const createFeed = useCreateICalFeed();
  const deleteFeed = useDeleteICalFeed();

  const feedsList = feeds as any[];
  const struttureList = strutture as any[];
  const camereList = tutteLeCamere as any[];

  const [showAdd, setShowAdd] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [newStruttura, setNewStruttura] = useState("");
  const [newCamera, setNewCamera] = useState("");
  const [newPiattaforma, setNewPiattaforma] = useState<Piattaforma>("Airbnb");
  const [copied, setCopied] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);

  const camerePerStruttura = useMemo(
    () => camereList.filter((c: any) => c.struttura_id === newStruttura || c.struttura_id === Number(newStruttura)),
    [camereList, newStruttura]
  );

  const selectedCamera = camerePerStruttura.find((c: any) => String(c.id) === newCamera);

  const handleCopy = (id: string, url: string) => {
    navigator.clipboard.writeText(url);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const resetForm = () => {
    setShowAdd(false); setNewUrl(""); setNewStruttura(""); setNewCamera(""); setNewPiattaforma("Airbnb");
  };

  const handleAdd = () => {
    if (!newUrl.trim() || !newStruttura || !newCamera) return;
    const nomeFeed = selectedCamera ? selectedCamera.nome : newPiattaforma;
    createFeed.mutate({
      struttura_id: newStruttura,
      nome: nomeFeed,
      url: newUrl,
      tipo: "import",
      attivo: true,
    }, { onSuccess: resetForm });
  };

  const feedAttivi = feedsList.filter((f: any) => f.attivo).length;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-foreground">Sincronizzazione Canali</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{feedAttivi} feed attivi · {feedsList.length} totali</p>
        </div>
        <button onClick={() => setShowAdd(v => !v)}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-primary/90 transition-colors">
          <Plus className="h-4 w-4" /> Aggiungi feed
        </button>
      </div>

      {/* Riepilogo */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="rounded-xl border bg-emerald-50 border-emerald-200 text-emerald-700 p-4">
          <div className="text-2xl font-bold">{feedAttivi}</div>
          <div className="text-xs font-medium mt-0.5">Feed attivi</div>
        </div>
        <div className="rounded-xl border bg-blue-50 border-blue-200 text-blue-700 p-4">
          <div className="text-2xl font-bold">{feedsList.length}</div>
          <div className="text-xs font-medium mt-0.5">Feed totali</div>
        </div>
      </div>

      {/* Form aggiungi feed */}
      {showAdd && (
        <div className="bg-card border border-primary/30 rounded-2xl p-5 mb-5">
          <h3 className="font-semibold text-foreground mb-4">Nuovo feed iCal</h3>
          <div className="space-y-3">

            {/* Struttura */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Struttura *</label>
              <div className="relative">
                <select value={newStruttura} onChange={e => { setNewStruttura(e.target.value); setNewCamera(""); }}
                  className="w-full appearance-none border border-border rounded-xl px-3.5 py-2.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 pr-8">
                  <option value="">Seleziona struttura…</option>
                  {struttureList.map((s: any) => <option key={s.id} value={s.id}>{s.nome}</option>)}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              </div>
            </div>

            {/* Camera — dipende dalla struttura */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Camera *</label>
              <div className="relative">
                <select value={newCamera} onChange={e => setNewCamera(e.target.value)}
                  disabled={!newStruttura}
                  className="w-full appearance-none border border-border rounded-xl px-3.5 py-2.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 pr-8 disabled:opacity-50">
                  <option value="">{newStruttura ? "Seleziona camera…" : "Prima seleziona una struttura"}</option>
                  {camerePerStruttura.map((c: any) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              </div>
              {selectedCamera && (
                <p className="text-xs text-muted-foreground mt-1">
                  Nome feed: <strong>{selectedCamera.nome}</strong>
                </p>
              )}
            </div>

            {/* Piattaforma con loghi */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-2 block">Piattaforma *</label>
              <div className="grid grid-cols-3 gap-2">
                {PIATTAFORME.map(p => (
                  <button key={p.id} type="button"
                    onClick={() => setNewPiattaforma(p.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${
                      newPiattaforma === p.id
                        ? "border-primary bg-primary/5 text-primary"
                        : "border-border text-foreground hover:border-primary/40"
                    }`}>
                    {p.logo}
                    <span className="truncate text-xs">{p.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* URL */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">URL iCal *</label>
              <input value={newUrl} onChange={e => setNewUrl(e.target.value)}
                className="w-full border border-border rounded-xl px-3.5 py-2.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="https://www.airbnb.com/calendar/ical/..." />
            </div>

            <div className="bg-muted/40 rounded-xl p-3 text-xs text-muted-foreground">
              <strong>Come trovare il link iCal:</strong> Airbnb → Calendario → Esporta · Booking.com → Proprietà → Calendario → Sincronizza
            </div>

            <div className="flex gap-2">
              <button onClick={handleAdd}
                disabled={!newStruttura || !newCamera || !newUrl.trim() || createFeed.isPending}
                className="flex-1 bg-primary text-primary-foreground py-2.5 rounded-xl text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50">
                {createFeed.isPending ? "Salvataggio..." : "Collega"}
              </button>
              <button onClick={resetForm}
                className="flex-1 border border-border py-2.5 rounded-xl text-sm text-muted-foreground hover:bg-muted/40 transition-colors">
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lista feed per struttura */}
      {feedsList.length === 0 && (
        <div className="bg-card rounded-2xl border border-border p-14 text-center">
          <Link2 className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" />
          <p className="font-medium text-muted-foreground">Nessun feed configurato</p>
          <p className="text-sm text-muted-foreground/70 mt-1">Aggiungi un feed iCal per sincronizzare le prenotazioni</p>
        </div>
      )}

      <div className="space-y-5">
        {struttureList.filter((s: any) => feedsList.some((f: any) => f.struttura_id === s.id)).map((s: any) => {
          const feedStruttura = feedsList.filter((f: any) => f.struttura_id === s.id);
          return (
            <div key={s.id}>
              <div className="flex items-center gap-2 mb-2.5">
                <div className="flex items-center justify-center w-6 h-6 rounded-md bg-primary/10 flex-shrink-0">
                  <Building2 className="h-3.5 w-3.5 text-primary" />
                </div>
                <span className="font-semibold text-foreground text-sm">{s.nome}</span>
                <span className="text-xs text-muted-foreground">{feedStruttura.length} feed</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {feedStruttura.map((f: any) => (
                  <div key={f.id} className="bg-card border border-border rounded-2xl p-4 hover:shadow-sm transition-shadow">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-xl bg-muted/60 flex items-center justify-center flex-shrink-0">
                          {piattaformaLogo(f.piattaforma ?? "Altro")}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-sm text-foreground leading-tight truncate">{f.nome}</p>
                          <p className="text-[11px] text-muted-foreground">{f.piattaforma ?? "—"}</p>
                        </div>
                      </div>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1 flex-shrink-0 ${f.attivo ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : "bg-muted text-muted-foreground"}`}>
                        {f.attivo ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                        {f.attivo ? "Attivo" : "Inattivo"}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-muted/40 rounded-xl px-2.5 py-1.5 mb-3">
                      <Link2 className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                      <span className="text-[11px] text-muted-foreground truncate flex-1">{f.url}</span>
                      <button onClick={() => handleCopy(f.id, f.url)} className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors">
                        {copied === f.id ? <CheckCircle2 className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
                      </button>
                    </div>
                    <div className="flex gap-2 pt-2.5 border-t border-border">
                      <button className="flex-1 flex items-center justify-center gap-1.5 border border-border rounded-xl py-1.5 text-xs text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                        <RefreshCw className="h-3 w-3" /> Sincronizza
                      </button>
                      {confirmDel === f.id ? (
                        <>
                          <button onClick={() => deleteFeed.mutate(f.id, { onSuccess: () => setConfirmDel(null) })}
                            className="px-2.5 py-1.5 rounded-xl bg-red-500 text-white text-xs font-medium">
                            {deleteFeed.isPending ? "..." : "Sì"}
                          </button>
                          <button onClick={() => setConfirmDel(null)}
                            className="px-2.5 py-1.5 rounded-xl border border-border text-xs text-muted-foreground">
                            No
                          </button>
                        </>
                      ) : (
                        <button onClick={() => setConfirmDel(f.id)}
                          className="flex items-center justify-center border border-border rounded-xl px-2.5 py-1.5 text-red-400 hover:border-red-300 hover:bg-red-50 transition-colors">
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                <button onClick={() => { setNewStruttura(s.id); setShowAdd(true); }}
                  className="border-2 border-dashed border-border rounded-2xl p-4 flex flex-col items-center justify-center gap-2 text-muted-foreground hover:border-primary hover:text-primary transition-colors min-h-[120px]">
                  <Plus className="h-4 w-4" />
                  <span className="text-xs font-medium">Aggiungi feed</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
